import { useEffect, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { Search } from 'lucide-react';
import {
  AttendanceApi,
  StaffAttendanceSummaryRow,
  StaffRosterRole,
  StaffRosterRow,
} from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Input, Select } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';
import { cn } from '@/lib/cn';
import { useDefaultYear } from './useDefaultYear';
import { StaffTypeBadge } from './staffTypeBadge';

const rosterColumnHelper = createColumnHelper<StaffRosterRow>();
const summaryColumnHelper = createColumnHelper<StaffAttendanceSummaryRow>();

const ROLE_LABEL: Record<StaffRosterRole, string> = { teacher: 'Teacher', staff: 'Staff', admin: 'Admin' };

const SUMMARY_BASE_COLUMNS = [
  summaryColumnHelper.accessor('personName', { header: 'Name' }),
  summaryColumnHelper.accessor('present', { header: 'Present', cell: (ctx) => <Badge tone="green">{ctx.getValue()}</Badge> }),
  summaryColumnHelper.accessor('absent', { header: 'Absent', cell: (ctx) => <Badge tone="red">{ctx.getValue()}</Badge> }),
  summaryColumnHelper.accessor('other', { header: 'Other', cell: (ctx) => <Badge tone="slate">{ctx.getValue()}</Badge> }),
];

export function StaffAttendancePage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<'daily' | 'summary'>('daily');
  const { selectedYear, isLoading: yearLoading, hasAnyData } = useDefaultYear();

  const listDt = useDataTable({ defaultLimit: 20, defaultSortBy: 'personName', defaultSortOrder: 'asc' });
  const summaryDt = useDataTable({ defaultLimit: 20, defaultSortBy: 'personName', defaultSortOrder: 'asc' });

  const [role, setRole] = useState<StaffRosterRole | ''>('');
  // Never seeded from the academic year's end_date — staff_attendance has no
  // academic_year_id at all, and a year's end_date sits far beyond any real
  // data (e.g. 2027-05-31 when the last real row is 2026-06-28), which
  // opens the page on an empty date. Left blank so the API call omits
  // `date` and the backend computes its own "most recent date with data"
  // default — adopted here once the response confirms which date that was.
  const [date, setDate] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    if (selectedYear) {
      setStartDate((d) => d || selectedYear.startDate.slice(0, 10));
      setEndDate((d) => d || selectedYear.endDate.slice(0, 10));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear?.id]);

  const { data: listData, isFetching: listFetching } = useQuery({
    queryKey: ['attendance', 'staff-roster', listDt.params, role, date],
    queryFn: () => AttendanceApi.staffRoster({ ...listDt.params, role: role || undefined, date: date || undefined }),
    enabled: tab === 'daily' && !yearLoading,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (!date && listData?.items.length) setDate(listData.items[0].date);
  }, [date, listData]);

  const { data: summaryData, isFetching: summaryFetching } = useQuery({
    queryKey: ['attendance', 'staff-summary', summaryDt.params, startDate, endDate],
    queryFn: () =>
      AttendanceApi.staffSummary({ ...summaryDt.params, startDate: startDate || undefined, endDate: endDate || undefined }),
    enabled: tab === 'summary' && !yearLoading,
    placeholderData: keepPreviousData,
  });

  const summaryColumns = [
    summaryColumnHelper.display({
      id: 'no',
      header: 'No.',
      enableSorting: false,
      cell: (info) => (
        <span className="text-slate-400">{summaryDt.pagination.pageIndex * summaryDt.pagination.pageSize + info.row.index + 1}</span>
      ),
    }),
    ...SUMMARY_BASE_COLUMNS,
  ];

  const { data: people } = useQuery({
    queryKey: ['attendance', 'staff-people'],
    queryFn: () => AttendanceApi.staffPeople(),
  });

  const [markPersonId, setMarkPersonId] = useState('');
  const [markType, setMarkType] = useState<0 | 1>(1);
  const [markDate, setMarkDate] = useState('');

  useEffect(() => {
    if (!markDate && date) setMarkDate(date);
  }, [date, markDate]);

  const mark = useMutation({
    mutationFn: () => AttendanceApi.markStaffAttendance({ personId: markPersonId, type: markType, date: markDate }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['attendance', 'staff-roster'] });
      qc.invalidateQueries({ queryKey: ['attendance', 'staff-summary'] });
      setMarkPersonId('');
    },
  });

  const rosterColumns = [
    rosterColumnHelper.display({
      id: 'no',
      header: 'No.',
      enableSorting: false,
      cell: (info) => (
        <span className="text-slate-400">{listDt.pagination.pageIndex * listDt.pagination.pageSize + info.row.index + 1}</span>
      ),
    }),
    rosterColumnHelper.accessor('personName', {
      header: 'Name',
      cell: (ctx) => (
        <span className="inline-flex items-center gap-2">
          {ctx.getValue()}
          <Badge tone="slate" className="text-[10px]">
            {ROLE_LABEL[ctx.row.original.role]}
          </Badge>
        </span>
      ),
    }),
    rosterColumnHelper.accessor('typeLabel', {
      header: 'Type',
      cell: (ctx) =>
        ctx.row.original.type === null ? (
          <span className="text-xs uppercase tracking-wider text-amber-600">Not Marked</span>
        ) : (
          <StaffTypeBadge type={ctx.row.original.type} label={ctx.getValue()} />
        ),
    }),
    rosterColumnHelper.accessor('time', { header: 'Time', cell: (ctx) => ctx.getValue() ?? '—' }),
    rosterColumnHelper.accessor('remarks', { header: 'Remarks', cell: (ctx) => ctx.getValue() ?? '—' }),
    rosterColumnHelper.accessor('addedByName', { header: 'Added By', cell: (ctx) => ctx.getValue() ?? '—' }),
  ];

  return (
    <>
      <PageHeader title="View Attendance (Staff)" description="Mark and review daily teacher/staff attendance, and range summaries." />

      {/* Create Teacher/Staff Attendance */}
      <div className="card mb-6 p-4">
        <h3 className="mb-3 text-sm font-semibold text-slate-700">Create Teacher/Staff Attendance</h3>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Staff / Teacher</label>
            <Select className="!w-56" value={markPersonId} onChange={(e) => setMarkPersonId(e.target.value)}>
              <option value="">Select Staff / Teacher</option>
              {(people ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({ROLE_LABEL[p.role]})
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Type</label>
            <div className="flex h-[38px] items-center gap-3">
              <label className="flex items-center gap-1.5 text-sm">
                <input type="radio" checked={markType === 1} onChange={() => setMarkType(1)} /> Present
              </label>
              <label className="flex items-center gap-1.5 text-sm">
                <input type="radio" checked={markType === 0} onChange={() => setMarkType(0)} /> Absent
              </label>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Date</label>
            <Input type="date" className="!w-40" value={markDate} onChange={(e) => setMarkDate(e.target.value)} />
          </div>
          <button
            className="btn-primary"
            disabled={!markPersonId || !markDate || mark.isPending}
            onClick={() => mark.mutate()}
          >
            {mark.isPending ? 'Saving…' : 'Submit'}
          </button>
        </div>
        {mark.isSuccess && (
          <div className="mt-3 rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">✓ Attendance saved.</div>
        )}
        {mark.error && (
          <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">Something went wrong. Please try again.</div>
        )}
      </div>

      <div className="mb-4 flex items-center gap-1 border-b border-slate-200">
        {(['daily', 'summary'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={cn(
              'border-b-2 px-4 py-2 text-sm font-medium',
              tab === t
                ? 'border-brand-600 text-brand-700'
                : 'border-transparent text-slate-500 hover:text-slate-700',
            )}
          >
            {t === 'daily' ? 'View Attendance' : 'Summary'}
          </button>
        ))}
      </div>

      {tab === 'daily' ? (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                value={listDt.searchInput}
                onChange={(e) => listDt.setSearchInput(e.target.value)}
                placeholder="Search by name"
                className="pl-9"
              />
            </div>
            <Input type="date" className="!w-40" value={date} onChange={(e) => setDate(e.target.value)} />
            <Select className="!w-48" value={role} onChange={(e) => setRole(e.target.value as StaffRosterRole | '')}>
              <option value="">All (Teacher + Staff + Admin)</option>
              <option value="teacher">Teacher</option>
              <option value="staff">Staff</option>
              <option value="admin">Admin</option>
            </Select>
          </div>
          {!hasAnyData && !yearLoading ? (
            <div className="card p-8 text-center text-slate-500">No attendance recorded for the selected filters.</div>
          ) : (
            <DataTable
              columns={rosterColumns}
              result={listData}
              state={listDt}
              loading={listFetching}
              emptyMessage="No attendance recorded for the selected filters."
              getRowId={(row) => row.personId}
            />
          )}
        </>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                value={summaryDt.searchInput}
                onChange={(e) => summaryDt.setSearchInput(e.target.value)}
                placeholder="Search by name"
                className="pl-9"
              />
            </div>
            <Input type="date" className="!w-40" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            <span className="text-sm text-slate-400">to</span>
            <Input type="date" className="!w-40" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
          {!hasAnyData && !yearLoading ? (
            <div className="card p-8 text-center text-slate-500">No attendance recorded for the selected filters.</div>
          ) : (
            <DataTable
              columns={summaryColumns}
              result={summaryData}
              state={summaryDt}
              loading={summaryFetching}
              emptyMessage="No attendance recorded for the selected filters."
              getRowId={(row) => row.personId}
            />
          )}
        </>
      )}
    </>
  );
}
